import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../../utils/api';
import { COUNTRIES } from '../../utils/countries';
import { callAuthPostWithFallback, __internal as authFallbackInternal } from '../../utils/authFallback';
import {
  NOTIFICATION_DEFAULTS,
  NOTIFICATION_TOGGLE_CONFIG,
  getNotificationMeta,
  normalizeNotificationPreferences
} from '../../constants/notificationPreferences';

import {
  User,
  Mail,
  MapPin,
  Lock,
  CreditCard,
  Shield,
  Bell,
  Package,
  Eye,
  EyeOff,
  Save,
  Edit,
  X,
  CheckCircle,
  AlertCircle,
  Calendar,
  DollarSign,
  Download,
  Key,
  History,
  Users,
  Plus,
  Trash2,
  RefreshCw,
  ExternalLink,
  ChevronUp,
  ChevronDown,
  AlertTriangle
} from 'lucide-react';
import { extractBusinessProfile } from '../../utils/contactNormalization';

// Initialize Stripe - must be at module level for use in PaymentMethodSetupModal
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_51S13rxBnBT7CggSVggNOdJmGCBnUmZOdV3z5psTElNtVIBIJC1kJWsjbBwDxUG35yhjwAeR7bJk431J578QNABRQ008FawJFLT');

const PRO_PLAN_FEATURES = [
  'Chat functionality',
  'Up to 5 user accounts',
  'Advanced analytics',
  'Priority support',
  'All Essential features'
];

const ESSENTIAL_PLAN_FEATURES = [
  'Send and receive B2B invoices',
  'Automatic invoice approval (7-day rule)',
  'Automatic payment collection',
  'Track payment statuses',
  'Analytics dashboard',
  '1 user account'
];

const PRO_PLAN_FEATURE_PREVIEW = PRO_PLAN_FEATURES.slice(0, 4);
const ESSENTIAL_PLAN_FEATURE_PREVIEW = ESSENTIAL_PLAN_FEATURES.slice(0, 3);
const VALID_TAB_IDS = ['account', 'payment', 'subscription', 'notifications', 'security', 'users'];

function isTrialActive(status, trialEnds) {
  if (typeof status !== 'string') {
    return false;
  }

  const normalizedStatus = status.toLowerCase();
  if (normalizedStatus !== 'trialing' && normalizedStatus !== 'free_trial') {
    return false;
  }

  if (!trialEnds) {
    return false;
  }

  const parsedDate = new Date(trialEnds);
  if (Number.isNaN(parsedDate.getTime())) {
    return false;
  }

  return parsedDate.getTime() >= Date.now();
}

function formatSubscriptionStatus(status, trialEnds) {
  if (!status) {
    return 'Unknown';
  }

  const normalized = status.toLowerCase();
  const statusMap = {
    active: 'Active',
    pending: 'Pending',
    cancelled: 'Cancelled',
    canceled: 'Cancelled',
    inactive: 'Inactive',
    past_due: 'Past Due',
    unpaid: 'Unpaid',
    paused: 'Paused',
    suspended: 'Suspended'
  };

  if (normalized === 'trialing' || normalized === 'free_trial') {
    return isTrialActive(status, trialEnds) ? 'Trialing' : 'Active';
  }

  if (statusMap[normalized]) {
    return statusMap[normalized];
  }

  return normalized
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const normalizePlan = (plan) => {
  const normalized = String(plan || '').toLowerCase();
  if (normalized === 'pro') return 'pro';
  if (normalized === 'none' || normalized === 'no plan') return 'none';
  return 'essential';
};

const formatPlanLabel = (plan) => {
  const normalized = normalizePlan(plan);
  if (normalized === 'pro') return 'Pro';
  if (normalized === 'none') return 'No Plan';
  return 'Essential';
};

const getPlanPrice = (plan) => {
  const normalized = normalizePlan(plan);
  if (normalized === 'pro') return 49.99;
  if (normalized === 'none') return 0;
  return 39.99;
};

const getPlanFeatures = (plan) => {
  const normalized = normalizePlan(plan);
  if (normalized === 'pro') {
    return [...PRO_PLAN_FEATURES];
  }
  if (normalized === 'none') {
    return [];
  }
  return [...ESSENTIAL_PLAN_FEATURES];
};

const Settings = () => {
  const {
    user,
    logout,
    notificationPreferences: initialNotificationPreferences,
    setNotificationPreferences,
    setNotificationDelivery,
    updateProfile
  } = useAuth();
  const [searchParams] = useSearchParams();
  // Only log on mount, not every render
  const hasLoggedMount = useRef(false);
  if (!hasLoggedMount.current && user?.email) {
    console.log('⚙️ Settings component loaded for user:', user?.email, 'role:', user?.role);
    hasLoggedMount.current = true;
  }
  
  const normalizedUserStatus = String(user?.status || '').toLowerCase();
  const normalizedSubscriptionStatus = String(user?.subscriptionStatus || '').toLowerCase();
  const isInactiveAccount =
    normalizedUserStatus === 'inactive' ||
    normalizedSubscriptionStatus === 'cancelled';

  const normalizeTabId = (tab) => {
    if (!tab) return null;
    const normalized = String(tab).toLowerCase();
    return VALID_TAB_IDS.includes(normalized) ? normalized : null;
  };

  const getInitialActiveTab = () => {
    const paramTab = normalizeTabId(searchParams.get('tab'));
    if (paramTab) {
      return paramTab;
    }
    try {
      const stored = localStorage.getItem('settings_active_tab');
      return normalizeTabId(stored) || 'account';
    } catch (error) {
      console.debug('Unable to read settings_active_tab from localStorage:', error);
      return 'account';
    }
  };

  const [activeTab, setActiveTab] = useState(() => {
    return getInitialActiveTab();
  });
  useEffect(() => {
    if (activeTab) {
      try {
        localStorage.setItem('settings_active_tab', activeTab);
      } catch (error) {
        console.debug('Unable to persist settings_active_tab to localStorage:', error);
      }
    }
  }, [activeTab]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const buildAccountInfo = useCallback(
    (sourceUser) => {
      const businessProfile = extractBusinessProfile(sourceUser);
      return {
        firstName: sourceUser?.firstName || '',
        lastName: sourceUser?.lastName || '',
        email: sourceUser?.email || '',
        companyName: sourceUser?.companyName || '',
        phone: sourceUser?.phone || '',
        address: {
          street: businessProfile.address || '',
          city: businessProfile.city || '',
          state: businessProfile.state || '',
          zipCode: businessProfile.zipCode || '',
          country: businessProfile.country || 'United Kingdom'
        }
      };
    },
    []
  );

  // Account Info State
  const [accountInfo, setAccountInfo] = useState(() => buildAccountInfo(user));
  const [isEditingAccount, setIsEditingAccount] = useState(false);

  useEffect(() => {
    if (!user || isEditingAccount) {
      return;
    }

    setAccountInfo(buildAccountInfo(user));
  }, [user, isEditingAccount, buildAccountInfo]);

  // Track if we've already refreshed profile to prevent repeated calls
  const lastRefreshUserIdRef = useRef(null);
  
  useEffect(() => {
    const userId = user?.id;
    // Only refresh if userId actually changed, not on every render
    if (!userId || userId === lastRefreshUserIdRef.current) {
      return;
    }
    
    lastRefreshUserIdRef.current = userId;
    let isMounted = true;

    const refreshProfile = async () => {
      try {
        const response = await api.get('/users/profile', { __skipAuthTokenPurge: true });
        const latestUser = response.data?.user;
        if (!latestUser || !isMounted) {
          return;
        }

        updateProfile(latestUser);
        if (!isEditingAccount) {
          setAccountInfo(buildAccountInfo(latestUser));
        }
      } catch (error) {
        console.warn('[Settings] Failed to refresh profile data:', error?.response?.data || error.message);
      }
    };

    refreshProfile();

    return () => {
      isMounted = false;
    };
  }, [user?.id, isEditingAccount]); // Removed buildAccountInfo and updateProfile from dependencies

  // Password State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState(false);

  // Payment Authorization State (DEPRECATED - now using Stripe Connect)
  const [paymentInfo, setPaymentInfo] = useState({
    bankName: 'Barclays Bank',
    accountNumber: '****1234',
    sortCode: '20-00-00',
    accountHolder: user?.firstName + ' ' + user?.lastName,
    paymentMethod: 'bank_transfer'
  });
  const [isEditingPayment, setIsEditingPayment] = useState(false);

  // Stripe Connect configuration - DO NOT REMOVE
  const [stripeAccountStatus, setStripeAccountStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(true); // Start as loading to prevent flash of "No Stripe Connected"
  const [stripePaymentInfo, setStripePaymentInfo] = useState(null);
  const [showAccountDetails, setShowAccountDetails] = useState(false);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const [paymentMethodLoading, setPaymentMethodLoading] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);

  const isSandboxHost =
    typeof window !== 'undefined' &&
    window.location?.hostname &&
    window.location.hostname.includes('sandbox');

  // Subscription State - Use actual user data
  const [subscription, setSubscription] = useState({
    plan: formatPlanLabel(user?.subscriptionPlan),
    price: getPlanPrice(user?.subscriptionPlan),
    currency: 'GBP',
    status: user?.subscriptionStatus || 'active',
    nextBilling: user?.subscriptionEndDate || null,
    trialEnds: user?.subscriptionTrialEnds || user?.subscriptionTrialEndDate || null,
    features: getPlanFeatures(user?.subscriptionPlan),
    lastPlan: formatPlanLabel(user?.subscriptionPlan)
  });

  useEffect(() => {
    setSubscription(prev => ({
      ...prev,
      plan: formatPlanLabel(user?.subscriptionPlan),
      price: getPlanPrice(user?.subscriptionPlan),
      status: user?.subscriptionStatus || prev.status,
      nextBilling: user?.subscriptionEndDate || null,
      trialEnds: user?.subscriptionTrialEnds || user?.subscriptionTrialEndDate || null,
      features: getPlanFeatures(user?.subscriptionPlan),
      lastPlan: formatPlanLabel(user?.subscriptionPlan || prev.lastPlan)
    }));
  }, [
    user?.subscriptionPlan,
    user?.subscriptionStatus,
    user?.subscriptionEndDate,
    user?.subscriptionTrialEnds,
    user?.subscriptionTrialEndDate
  ]);

  // Notifications State
  const [notifications, setNotifications] = useState(() =>
    normalizeNotificationPreferences(initialNotificationPreferences)
  );

  useEffect(() => {
    setNotifications(normalizeNotificationPreferences(initialNotificationPreferences));
  }, [initialNotificationPreferences]);

  // Security State
  const [security, setSecurity] = useState({
    twoFactor: {
      enabled: false,
      status: 'pending',
      gracePeriodEndsAt: null,
      daysRemaining: 0,
      verifiedAt: null,
      firstVerifiedAt: null,
      hasCompletedInitialSetup: false,
      reconfigureRequired: false
    },
    loginHistory: []
  });
  const [twoFactorModalOpen, setTwoFactorModalOpen] = useState(false);
  const [twoFactorSetupData, setTwoFactorSetupData] = useState(null);
  const [twoFactorSetupLoading, setTwoFactorSetupLoading] = useState(false);
  const [planChangeModal, setPlanChangeModal] = useState({
    open: false,
    plan: null,
    confirming: false
  });
  const [cancelModal, setCancelModal] = useState({
    open: false,
    confirming: false
  });
  const [twoFactorConfirmCode, setTwoFactorConfirmCode] = useState('');
  const [twoFactorConfirmError, setTwoFactorConfirmError] = useState('');
  const [twoFactorReconfiguring, setTwoFactorReconfiguring] = useState(false);
  const twoFactorPortalRef = useRef(null);

  useEffect(() => {
    console.log('[Settings] twoFactorModalOpen:', twoFactorModalOpen);
  }, [twoFactorModalOpen]);

  useEffect(() => {
    if (twoFactorSetupData) {
      console.log('[Settings] twoFactorSetupData updated:', twoFactorSetupData);
    }
  }, [twoFactorSetupData]);

  useEffect(() => {
    if (security.twoFactor.enabled) {
      setTwoFactorReconfiguring(false);
    }
  }, [security.twoFactor.enabled]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (!twoFactorPortalRef.current) {
      const existing = document.getElementById('twofactor-portal-root');
      if (existing) {
        twoFactorPortalRef.current = existing;
      } else {
        const node = document.createElement('div');
        node.id = 'twofactor-portal-root';
        node.dataset.twofactorPortalRoot = 'true';
        twoFactorPortalRef.current = node;
      }
    }

    const portalNode = twoFactorPortalRef.current;
    if (portalNode && !portalNode.isConnected) {
      document.body.appendChild(portalNode);
    }

    return () => {
      if (
        portalNode &&
        portalNode.parentNode &&
        portalNode.dataset.twofactorPortalRoot === 'true'
      ) {
        portalNode.parentNode.removeChild(portalNode);
      }
    };
  }, []);

  // Additional Users State
  const [additionalUsers, setAdditionalUsers] = useState([]);
  const [newUserForm, setNewUserForm] = useState({
    name: '',
    email: ''
  });
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  
  // Check if user is main account holder (has full access to all settings)
  // Additional users have role === 'additional_user'
  const isMainAccountHolder = user?.role !== 'additional_user';


  const tabs = useMemo(() => {
    // Additional users see Account Info and Security tabs only
    if (!isMainAccountHolder) {
      return [
        { id: 'account', name: 'Account Info', icon: User },
        { id: 'security', name: 'Security', icon: Shield }
      ];
    }
    
    // Main users see all tabs
    const baseTabs = [
      { id: 'account', name: 'Account Info', icon: User },
      { id: 'payment', name: 'Stripe Connected Account', icon: CreditCard },
      { id: 'subscription', name: 'Subscription', icon: Package },
      { id: 'notifications', name: 'Notifications', icon: Bell },
      { id: 'security', name: 'Security', icon: Shield },
      { id: 'users', name: 'Additional Users', icon: Users }
    ];
    if (isInactiveAccount) {
      return baseTabs.filter(tab => tab.id === 'subscription');
    }
    return baseTabs;
  }, [isMainAccountHolder, isInactiveAccount]);
  const availableTabIds = tabs.map(tab => tab.id);
  const subscriptionStatusLabel = formatSubscriptionStatus(subscription.status, subscription.trialEnds);
  const isProPlan = normalizePlan(subscription.plan) === 'pro';
  const isSubscriptionCancelled = String(subscription.status || '').toLowerCase() === 'cancelled';
  const isReactivationFlow = useMemo(
    () =>
      planChangeModal.plan === 'Essential' &&
      (isInactiveAccount || isSubscriptionCancelled),
    [planChangeModal.plan, isInactiveAccount, isSubscriptionCancelled]
  );

  useEffect(() => {
    if (isInactiveAccount) {
      if (activeTab !== 'subscription') {
        setActiveTab('subscription');
      }
      return;
    }
    const paramTab = normalizeTabId(searchParams.get('tab'));
    if (paramTab && paramTab !== activeTab) {
      setActiveTab(paramTab);
    }
  }, [isInactiveAccount, searchParams, activeTab]);

  useEffect(() => {
    if (!availableTabIds.includes(activeTab)) {
      setActiveTab(availableTabIds[0] || 'account');
    }
  }, [availableTabIds.join(','), activeTab]);

  const handleAccountSave = async () => {
    setLoading(true);
    try {
      // Flatten address object to match backend schema
      const updateData = {
        firstName: accountInfo.firstName,
        lastName: accountInfo.lastName,
        companyName: accountInfo.companyName,
        phone: accountInfo.phone,
        // Flatten address fields to match database columns
        address: accountInfo.address.street,
        city: accountInfo.address.city,
        state: accountInfo.address.state,
        zipCode: accountInfo.address.zipCode,
        country: accountInfo.address.country
      };
      
      console.log('💾 Saving account info:', updateData);
      
      // Use direct Lambda Function URL to bypass API Gateway's WAF issues with JWT tokens
      const token = localStorage.getItem('token');
      const lambdaResponse = await fetch('https://jihcfjgfzbcof7fsii3ks4zceq0zibvh.lambda-url.eu-west-2.on.aws/', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-http-method': 'PUT',
          'x-http-path': '/profile'
        },
        body: JSON.stringify(updateData)
      });
      
      if (!lambdaResponse.ok) {
        const errorData = await lambdaResponse.json();
        throw new Error(errorData.error || 'Failed to update profile');
      }
      
      setIsEditingAccount(false);
      setMessage({ type: 'success', text: 'Account information updated successfully!' });
      
      // Refresh user data
      window.location.reload();
    } catch (error) {
      console.error('❌ Failed to save account info:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update account information' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    if (passwordData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
      return;
    }

    setLoading(true);
    try {
      // Real API call to change password
      await callAuthPostWithFallback(api, {
        apiPath: '/users/change-password',
        fallbackPath: '/users/change-password',
        method: 'put',
        payload: {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        },
        allowFallbackOnProduction: true,
        forceFallback: true,
        fallbackBaseUrlOverride: authFallbackInternal.AUTH_LAMBDA_FUNCTION_URL
      });
      
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setMessage({ type: 'success', text: 'Password changed successfully!' });
    } catch (error) {
      if (error.response?.data?.message) {
        setMessage({ type: 'error', text: error.response.data.message });
      } else {
        setMessage({ type: 'error', text: 'Failed to change password. Please check your current password.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSave = async () => {
    setLoading(true);
    try {
      // TODO: API call to update payment info
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      setIsEditingPayment(false);
      setMessage({ type: 'success', text: 'Payment information updated successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update payment information' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionChange = async (newPlan) => {
    setLoading(true);
    try {
      console.log(`📦 Changing plan to: ${newPlan}`);
      
      // Call backend API to change subscription plan (use auth fallback to bypass API Gateway restrictions)
      const response = await callAuthPostWithFallback(api, {
        apiPath: '/subscription/change-plan',
        fallbackPath: '/subscription/change-plan',
        method: 'post',
        payload: {
          newPlan: newPlan.toLowerCase()
        },
        requireAuthToken: true,
        allowFallbackOnProduction: true,
        forceFallback: true,
        fallbackBaseUrlOverride: authFallbackInternal.AUTH_LAMBDA_FUNCTION_URL,
        additionalHeaders: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.success) {
        console.log('✅ Plan change successful:', response);
        
        const normalizedPlan = normalizePlan(newPlan);
        const planLabel = formatPlanLabel(newPlan);
        const updatedSubscription = response.subscription || {};
        const normalizedStatus = updatedSubscription.status || (normalizedPlan === 'pro' ? 'active' : 'active');
        const nextBillingDate = updatedSubscription.current_period_end
          ? new Date(updatedSubscription.current_period_end * 1000).toISOString()
          : subscription.nextBilling;
        const trialEndsAt = updatedSubscription.trial_end
          ? new Date(updatedSubscription.trial_end * 1000).toISOString()
          : subscription.trialEnds;

        // Update local state immediately
        setSubscription(prev => ({
          ...prev,
          plan: planLabel,
          price: getPlanPrice(newPlan),
          features: getPlanFeatures(newPlan),
          status: normalizedStatus,
          nextBilling: nextBillingDate,
          trialEnds: trialEndsAt,
          lastPlan: planLabel
        }));
        
        // Update user context to reflect new plan
        await updateProfile({
          subscriptionPlan: normalizedPlan,
          subscriptionStatus: normalizedStatus,
          status: 'active',
          subscriptionEndDate: nextBillingDate,
          subscriptionTrialEnds: trialEndsAt
        });
        
        setMessage({ 
          type: 'success', 
          text: `✅ ${response.subscription.changeType} successfully! Your plan is now ${planLabel}. Changes are effective immediately.` 
        });
      } else {
        const responseMessage = response?.message || response?.error;

        switch (response?.code) {
          case 'STRIPE_SUBSCRIPTION_REQUIRES_ACTION':
            setMessage({
              type: 'warning',
              text: responseMessage || 'Stripe needs additional authorisation to complete this change. Please check your email or payment method.'
            });
            break;
          case 'STRIPE_CUSTOMER_NOT_FOUND':
            setMessage({
              type: 'error',
              text: 'We could not find your Stripe customer record. Please contact support so we can reconnect your billing.'
            });
            break;
          case 'PLAN_CHANGE_REQUIRES_STRIPE_INVITE':
            setMessage({
              type: 'warning',
              text: responseMessage || 'Your Stripe account needs to be reconnected before changing plans.'
            });
            break;
          case 'STRIPE_SUBSCRIPTION_REQUIRES_PAYMENT_METHOD':
            setMessage({
              type: 'error',
              text: responseMessage || 'We could not charge your saved payment method. Please update the card in Stripe and retry.'
            });
            break;
          default:
            setMessage({ type: 'error', text: responseMessage || 'Failed to change plan' });
        }
      }
    } catch (error) {
      console.error('Error changing subscription:', error);
      const errorData = error.response?.data || {};
      const errorMessage = errorData.message || errorData.error || error.message;

      switch (errorData.code) {
        case 'STRIPE_SUBSCRIPTION_REQUIRES_ACTION':
          setMessage({
            type: 'warning',
            text: errorMessage || 'Stripe needs additional authorisation to complete this change. Please check your email or payment method.'
          });
          break;
        case 'STRIPE_SUBSCRIPTION_REQUIRES_PAYMENT_METHOD':
          setMessage({
            type: 'error',
            text: errorMessage || 'We could not charge your saved payment method. Please update the card in Stripe and retry.'
          });
          break;
        case 'STRIPE_CUSTOMER_NOT_FOUND':
          setMessage({
            type: 'error',
            text: 'We could not find your Stripe customer record. Please contact support so we can reconnect your billing.'
          });
          break;
        case 'PLAN_CHANGE_REQUIRES_STRIPE_INVITE':
          setMessage({
            type: 'warning',
            text: errorMessage || 'Your Stripe account needs to be reconnected before changing plans.'
          });
          break;
        default:
          setMessage({ 
            type: 'error', 
            text: errorMessage || 'Failed to change subscription. Please try again or contact support.' 
          });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelModal(prev => ({ ...prev, confirming: true }));
    try {
      const response = await callAuthPostWithFallback(api, {
        apiPath: '/subscription/cancel',
        fallbackPath: '/subscription/cancel',
        method: 'post',
        requireAuthToken: true,
        allowFallbackOnProduction: true,
        forceFallback: true,
        payload: {},
        fallbackBaseUrlOverride: authFallbackInternal.AUTH_LAMBDA_FUNCTION_URL
      });

      if (response?.success) {
        setSubscription(prev => ({
          ...prev,
          plan: 'None',
          price: 0,
          features: [],
          status: 'cancelled',
          nextBilling: null,
          trialEnds: null,
          lastPlan: response.subscription?.planName || prev.lastPlan || formatPlanLabel(prev.plan)
        }));

        await updateProfile({
          subscriptionStatus: 'cancelled',
          subscriptionPlan: 'essential',
          status: 'inactive'
        });

        setMessage({
          type: 'success',
          text: 'Your subscription has been cancelled. Your account is now inactive.'
        });
      } else {
        setMessage({
          type: 'error',
          text: response?.error || 'Failed to cancel subscription. Please contact support.'
        });
      }
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to cancel subscription. Please contact support.';
      setMessage({ type: 'error', text: message });
    } finally {
      setCancelModal({ open: false, confirming: false });
    }
  };

  const openPlanChangeModal = (plan) => {
    setPlanChangeModal({
      open: true,
      plan,
      confirming: false
    });
  };

  const closePlanChangeModal = () => {
    setPlanChangeModal({
      open: false,
      plan: null,
      confirming: false
    });
  };

  const confirmPlanChange = async () => {
    if (!planChangeModal.plan) {
      return;
    }

    setPlanChangeModal(prev => ({
      ...prev,
      confirming: true
    }));

    try {
      await handleSubscriptionChange(planChangeModal.plan);
    } finally {
      setPlanChangeModal({
        open: false,
        plan: null,
        confirming: false
      });
    }
  };

  const handleNotificationToggle = async (key) => {
    try {
      const meta = getNotificationMeta(key);
      const label = meta?.label || key.replace(/([A-Z])/g, ' $1').trim();
      const updatedPreferences = {
        ...notifications,
        [key]: !notifications[key]
      };
      
      // Update local state immediately for better UX
      setNotifications(updatedPreferences);
      
      // Save to backend with notification delivery settings using fallback-safe call
      await callAuthPostWithFallback(api, {
        apiPath: '/users/notification-preferences',
        fallbackPath: '/users/notification-preferences',
        method: 'put',
        authMode: 'query',
        payload: {
          preferences: updatedPreferences,
          deliveryMethods: {
            email: true, // Always enable email notifications
            inApp: true, // Always enable in-app notifications
            push: false  // Push notifications disabled for now
          },
          userEmail: user?.email,
          userName: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email
        }
      });

      if (key === 'chatMessages') {
        window.dispatchEvent(
          new CustomEvent('chatNotificationPreferenceChanged', {
            detail: { enabled: updatedPreferences[key] }
          })
        );
      }

      setNotificationPreferences(updatedPreferences);
      localStorage.setItem('notification_preferences', JSON.stringify(updatedPreferences));
      setNotificationDelivery({
        email: true,
        inApp: true,
        push: false
      });
      
      setMessage({ 
        type: 'success', 
        text: `${label} notifications ${updatedPreferences[key] ? 'enabled' : 'disabled'}.`
      });
    } catch (error) {
      // Revert local state on error
      setNotifications(prev => ({
        ...prev,
        [key]: !prev[key]
      }));
      const meta = getNotificationMeta(key);
      const label = meta?.label || key.replace(/([A-Z])/g, ' $1').trim();
      setMessage({ type: 'error', text: `Failed to update ${label.toLowerCase()} notifications` });
    }
  };

  // ==================== STRIPE CONNECT FUNCTIONS - DO NOT REMOVE ====================
  // Check Stripe Connect account status
  const checkStripeAccountStatus = async () => {
    try {
      const response = await api.get('/api/stripe-connect/account-status', {
        timeout: 10000 // 10 second timeout
      });
      console.log('Account status response:', response.data);
      console.log('Account data:', response.data.account);
      console.log('Charges enabled:', response.data.account?.chargesEnabled);
      console.log('Payouts enabled:', response.data.account?.payoutsEnabled);
      setStripeAccountStatus(response.data.account);
      setStripeLoading(false);
    } catch (error) {
      console.error('Error checking Stripe account status:', error);
      
      // Handle timeout gracefully - show helpful message instead of "No account"
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        console.warn('Stripe account status check timed out');
        setStripeAccountStatus({ 
          timeout: true, 
          message: 'Unable to verify account status. If you recently connected Stripe, your account is likely active. Check your Stripe dashboard to confirm.',
          helpLink: 'https://dashboard.stripe.com/connect/accounts'
        });
        setStripeLoading(false);
      } else if (error.response?.status === 404) {
        // No account found - this is expected for new users
        setStripeAccountStatus(null);
        setStripeLoading(false);
      } else if (error.code === 'NETWORK_ERROR' || !error.response || error.response?.status >= 500) {
        // Network/server error - show helpful message
        console.warn('Server temporarily unavailable');
        setStripeAccountStatus({ 
          timeout: true, 
          message: 'Unable to verify account status due to server timeout. Your Stripe account may be connected. Please check your Stripe dashboard.',
          helpLink: 'https://dashboard.stripe.com/connect/accounts'
        });
        setStripeLoading(false);
      } else {
        // Other errors
        setMessage({ type: 'error', text: 'Failed to check Stripe account status' });
        setStripeAccountStatus(null);
        setStripeLoading(false);
      }
    }
  };

  // Link Stripe Connect account
  const linkStripeAccount = async () => {
    setStripeLoading(true);
    setMessage({ type: 'info', text: 'Preparing Stripe Connect...' });

    try {
      // Check if account is already fully set up
      if (stripeAccountStatus && stripeAccountStatus.chargesEnabled && stripeAccountStatus.payoutsEnabled) {
        // Account is fully set up, redirect to Stripe dashboard
        window.open('https://dashboard.stripe.com/connect/accounts', '_blank');
        setMessage({ type: 'success', text: 'Opening Stripe dashboard...' });
        return;
      }

      // Account needs setup, try to get onboarding link
      let response;
      try {
        response = await api.post('/api/stripe-connect/onboarding-link');
        console.log('Onboarding link response:', response.data);
      } catch (error) {
        console.log('Onboarding link error:', error.response?.data);
        // If no account exists, create one first
        if (error.response?.status === 404 && error.response?.data?.error === 'No Stripe Connect account found') {
          console.log('Creating new account...');
          response = await api.post('/api/stripe-connect/create-account', {
            country: 'GB',
            businessType: 'individual'
          });
          console.log('Create account response:', response.data);

          // Now get the onboarding link
          response = await api.post('/api/stripe-connect/onboarding-link');
          console.log('New onboarding link response:', response.data);
        } else {
          throw error;
        }
      }

      // Redirect to Stripe onboarding
      const redirectUrl = response.data.accountLink || response.data.url;
      console.log('Redirecting to:', redirectUrl);
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        throw new Error('No redirect URL received from server');
      }
    } catch (error) {
      console.error('Error linking Stripe account:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to create onboarding link' 
      });
    } finally {
      setStripeLoading(false);
    }
  };

  // Check Stripe account status on component mount
  useEffect(() => {
    checkStripeAccountStatus();
    
    // Load Stripe payment info
    const fetchStripePaymentInfo = async () => {
      try {
        const paymentData = await api.get('/api/users/payment-info');
        if (paymentData.data.success) {
          setStripePaymentInfo(paymentData.data.paymentInfo);
        }
      } catch (error) {
        console.error('Error loading Stripe payment info:', error);
      }
    };
    
    fetchStripePaymentInfo();
    
    // Load default payment method
    const fetchDefaultPaymentMethod = async () => {
      try {
        const response = await api.get('/api/payment-methods/default');
        if (response.data.success) {
          setDefaultPaymentMethod(response.data.data);
        }
      } catch (error) {
        if (error.response?.status === 404) {
          // Check if payment method was deleted (not just missing)
          if (error.response?.data?.code === 'PAYMENT_METHOD_DELETED' || 
              error.response?.data?.code === 'CUSTOMER_DELETED') {
            // Payment method or customer was deleted from Stripe
            setDefaultPaymentMethod(null);
            setMessage({ 
              type: 'error', 
              text: 'Your payment method was removed from Stripe. Please add a new payment method to continue making payments.' 
            });
            console.warn('Payment method was deleted from Stripe:', error.response?.data?.message);
          }
          // Otherwise, 404 is expected if no payment method is set
        } else {
          console.error('Error loading default payment method:', error);
        }
      }
    };
    
    if (isMainAccountHolder) {
      fetchDefaultPaymentMethod();
    }
  }, [isMainAccountHolder]); // Run on mount and when account holder status changes

  // Update accountInfo when user data is loaded - only if not editing
  // Use user.id instead of user object to prevent re-renders when user object reference changes
  const prevUserIdRef = useRef(null);
  useEffect(() => {
    const userId = user?.id;
    const hasUserChanged = userId && userId !== prevUserIdRef.current;
    
    if (userId && !isEditingAccount && hasUserChanged) {
      prevUserIdRef.current = userId;
      
      setAccountInfo({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        companyName: user.companyName || '',
        phone: user.phone || '',
        address: {
          street: user.address || '',
          city: user.city || '',
          state: user.state || '',
          zipCode: user.zipCode || '',
          country: user.country || 'United Kingdom'
        }
      });
    }
  }, [user?.id, isEditingAccount]); // Only depend on user.id and isEditingAccount

const fetchSecurityData = useCallback(async () => {
  try {
    const response = await api.get('/users/security');
    if (response.data.success) {
      const twoFactorData = response.data.data.twoFactor || {};
      const normalizedTwoFactor = {
        enabled: Boolean(twoFactorData.enabled),
        status: twoFactorData.status || 'pending',
        gracePeriodEndsAt: twoFactorData.gracePeriodEndsAt || null,
        daysRemaining: Number.isFinite(twoFactorData.daysRemaining) ? twoFactorData.daysRemaining : 0,
        verifiedAt: twoFactorData.verifiedAt || null,
        firstVerifiedAt: twoFactorData.firstVerifiedAt || null,
        hasCompletedInitialSetup: Boolean(twoFactorData.hasCompletedInitialSetup),
        reconfigureRequired: Boolean(twoFactorData.reconfigureRequired)
      };

      setSecurity(prev => ({
        ...prev,
        twoFactor: normalizedTwoFactor,
        loginHistory: response.data.data.loginHistory || []
      }));
      setTwoFactorReconfiguring(normalizedTwoFactor.reconfigureRequired);
      return;
    }
  } catch (error) {
    console.error('Error loading security data:', error);
  }

  // Fallback: create current session log entry
  const userAgent = navigator.userAgent;
  let device = 'Unknown Browser';
  let os = 'Unknown OS';

  if (userAgent.includes('Chrome')) device = 'Chrome';
  else if (userAgent.includes('Safari')) device = 'Safari';
  else if (userAgent.includes('Firefox')) device = 'Firefox';
  else if (userAgent.includes('Edge')) device = 'Edge';

  if (userAgent.includes('Mac')) os = 'macOS';
  else if (userAgent.includes('Windows')) os = 'Windows';
  else if (userAgent.includes('Linux')) os = 'Linux';
  else if (userAgent.includes('iPhone')) os = 'iOS';
  else if (userAgent.includes('Android')) os = 'Android';

  const currentLogin = {
    date: new Date().toISOString(),
    device: `${device} on ${os}`,
    location: 'Loading...',
    ip: 'Loading...',
    success: true,
    loginType: 'client_login',
    userName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || 'Current session',
    userEmail: user?.email || ''
  };

  setSecurity(prev => ({
    ...prev,
    loginHistory: [currentLogin]
  }));

  // Note: Session logging endpoint not implemented in backend
  // Current session is displayed in UI from the above state update
}, [user]);

// Load security, notifications, and additional users - only once on mount
const hasLoadedData = useRef(false);
useEffect(() => {
  if (hasLoadedData.current || !user?.id) {
    return;
  }
  
  hasLoadedData.current = true;
  
  const loadNotificationPreferences = async () => {
      try {
      const response = await api.get('/users/notification-preferences');
        if (response.data.success) {
        const prefs = response.data.data || {};
        const normalized = normalizeNotificationPreferences(prefs);
        setNotifications(normalized);
        setNotificationPreferences(normalized);
        localStorage.setItem('notification_preferences', JSON.stringify(normalized));
        const delivery = response.data.deliveryMethods || {};
        setNotificationDelivery({
          email: delivery.email !== false,
          inApp: delivery.inApp !== false,
          push: delivery.push === true
        });
        }
      } catch (error) {
      console.error('Error loading notification preferences:', error);
      }
    };

    const loadAdditionalUsers = async () => {
      try {
        const response = await api.get('/users/additional-users');
        if (response.data.success) {
          setAdditionalUsers(response.data.data || []);
        } else {
          console.warn('⚠️ API returned success=false:', response.data);
        }
      } catch (error) {
        console.error('❌ Error loading additional users:', error);
        console.error('❌ Error details:', error.response?.data || error.message);
      }
    };

    (async () => {
      await Promise.all([
      fetchSecurityData(),
        loadNotificationPreferences(),
        loadAdditionalUsers(),
      ]);
  })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [user?.id]); // Only run when user.id changes (mount or user change)
  
  // Separate effect for retry logic to avoid stale closures
  useEffect(() => {
    // Only set up retry if we don't have an account status
    if (stripeAccountStatus) {
      // We have an account, no need to retry
      return;
    }
    
    // Retry every 30 seconds if no account status is found
    console.log('Setting up Stripe account status retry...');
    const retryInterval = setInterval(() => {
      console.log('Retrying Stripe account status check...');
      checkStripeAccountStatus();
    }, 30000);
    
    return () => {
      console.log('Clearing Stripe retry interval');
      clearInterval(retryInterval);
    };
  }, [stripeAccountStatus]); // Re-run when stripeAccountStatus changes
  // ==================== END STRIPE CONNECT FUNCTIONS ====================

  const handleStartTwoFactorSetup = async () => {
    // Allow both main users and additional users to set up their own 2FA
    try {
      const wasEnabled = Boolean(security.twoFactor.enabled);
      const hasCompletedInitialSetup = Boolean(security.twoFactor.hasCompletedInitialSetup);
      const enteringReconfigure = wasEnabled || hasCompletedInitialSetup;
      setTwoFactorReconfiguring(enteringReconfigure);

      setTwoFactorSetupLoading(true);
      setTwoFactorConfirmCode('');
      setTwoFactorConfirmError('');

      console.log('[Settings] Starting 2FA setup request');
      let data;
      try {
        const apiResponse = await api.post('/api/users/two-factor/setup', {}, { __skipAuthTokenPurge: true });
        data = apiResponse.data;
      } catch (primaryError) {
        console.warn('[Settings] 2FA setup via API proxy failed, using auth lambda fallback', primaryError);
        data = await callAuthPostWithFallback(api, {
          apiPath: '/users/two-factor/setup',
          fallbackPath: '/users/two-factor/setup',
          authMode: 'query',
          forceFallback: true,
          allowFallbackOnProduction: true,
        });
      }

      console.log('[Settings] 2FA setup response:', data);
      if (data?.success) {
        setTwoFactorSetupData(data.data);
        setTwoFactorModalOpen(true);
        window.dispatchEvent(new CustomEvent('twofactor:updated'));
        setMessage({
          type: 'info',
          text: enteringReconfigure
            ? 'Two-factor reconfiguration started. Scan the new QR code and verify the 6-digit code to continue using Receevit®.'
            : 'Two-factor setup started. Scan the QR code to finish securing your account.'
        });
        await fetchSecurityData().catch((err) =>
          console.error('Error refreshing security data after starting 2FA setup:', err)
        );
      } else {
        console.warn('[Settings] 2FA setup returned success=false', data);
        setMessage({ type: 'error', text: data?.error || 'Failed to start two-factor setup' });
      }
    } catch (error) {
      console.error('Error starting two-factor setup:', error);
      const errorText =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to start two-factor setup';
      setMessage({ type: 'error', text: errorText });
    } finally {
      setTwoFactorSetupLoading(false);
    }
  };

  const handleConfirmTwoFactorSetup = async () => {
    if (!twoFactorConfirmCode || twoFactorConfirmCode.trim().length < 6) {
      setTwoFactorConfirmError('Enter the 6-digit code from your authenticator app.');
      return;
    }

    try {
      setTwoFactorConfirmError('');
      setTwoFactorSetupLoading(true);

      console.log('[Settings] Verifying 2FA code');
      let data;
      try {
        const apiResponse = await api.post(
          '/api/users/two-factor/verify',
          {
            code: twoFactorConfirmCode.trim(),
          },
          {
            __skipAuthTokenPurge: true
          }
        );
        data = apiResponse.data;
      } catch (primaryError) {
        console.warn('[Settings] 2FA verify via API proxy failed, using auth lambda fallback', primaryError);
        data = await callAuthPostWithFallback(api, {
          apiPath: '/users/two-factor/verify',
          fallbackPath: '/users/two-factor/verify',
          payload: {
            code: twoFactorConfirmCode.trim(),
          },
          authMode: 'query',
          forceFallback: true,
          allowFallbackOnProduction: true
        });
      }

      console.log('[Settings] 2FA verify response:', data);
      if (data?.success) {
        const verifiedAt = new Date().toISOString();
        setSecurity(prev => ({
          ...prev,
          twoFactor: {
            enabled: true,
            status: 'enabled',
            gracePeriodEndsAt: prev.twoFactor.gracePeriodEndsAt,
            daysRemaining: 0,
            verifiedAt,
            firstVerifiedAt: prev.twoFactor.firstVerifiedAt || verifiedAt,
            hasCompletedInitialSetup: true,
            reconfigureRequired: false
          }
        }));

        setTwoFactorModalOpen(false);
        setTwoFactorSetupData(null);
        setTwoFactorConfirmCode('');
        setTwoFactorReconfiguring(false);
        setMessage({ type: 'success', text: 'Two-factor authentication enabled successfully!' });
        window.dispatchEvent(new CustomEvent('twofactor:updated'));
        await fetchSecurityData().catch((err) =>
          console.error('Error refreshing security data after verifying 2FA:', err)
        );
      } else {
        setTwoFactorConfirmError(data?.error || 'Verification failed. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying two-factor code:', error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Verification failed. Please try again.';
      setTwoFactorConfirmError(message);
    } finally {
      setTwoFactorSetupLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    if (!twoFactorSetupData?.backupCodes) {
      return;
    }

    const blob = new Blob([
      'Receevit® Two-Factor Authentication Backup Codes\n\n',
      ...twoFactorSetupData.backupCodes.map(code => `${code}\n`),
      '\nStore these codes in a safe place. Each code can be used once.'
    ], { type: 'text/plain;charset=utf-8' });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'receevit-backup-codes.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Additional Users Handlers
  const handleAddUser = async () => {
    if (!newUserForm.name.trim() || !newUserForm.email.trim()) {
      setMessage({ type: 'error', text: 'Please fill in both name and email fields' });
      return;
    }

    if (additionalUsers.length >= 4) {
      setMessage({ type: 'error', text: 'Maximum of 4 additional users allowed on Pro plan' });
      return;
    }

    setLoading(true);
    try {
      const newUser = {
        name: newUserForm.name.trim(),
        email: newUserForm.email.trim(),
        role: 'User', // Default role
        status: 'Pending Invite'
      };
      
      // Send invitation to backend
      const response = await api.post('/users/additional-users/invite', {
        ...newUser,
        companyName: user?.companyName || 'Your Company',
        inviterName: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email,
        invitationMessage: `You've been invited to join ${user?.companyName || 'our company'} on Receevit®. Click the link below to accept your invitation and set up your account.`
      });
      
      if (response.data.success) {
        // Add to local state
        setAdditionalUsers(prev => [...prev, {
          id: response.data.data.id,
          ...newUser,
          invitedAt: new Date().toISOString()
        }]);
        
        // Reset form
        setNewUserForm({ name: '', email: '' });
        setShowAddUserForm(false);
        
        setMessage({ type: 'success', text: 'User invitation sent successfully!' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to send invite. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    setLoading(true);
    try {
      // Remove user from backend
      await api.delete(`/users/additional-users/${userId}`);
      
      // Remove from local state
      setAdditionalUsers(prev => prev.filter(user => user.id !== userId));
      setMessage({ type: 'success', text: 'User removed successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to remove user. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvite = async (userId) => {
    setLoading(true);
    try {
      // Resend invitation via backend
      await api.post(`/users/additional-users/${userId}/resend`);
      
      setMessage({ type: 'success', text: 'Invite resent successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to resend invite. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    // Database stores amounts in pounds as DECIMAL(10,2)
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-GB');
  };

  const formatFailureReason = (reason) => {
    if (!reason) return 'Unknown reason';
    const normalized = reason.toUpperCase();
    const lookup = {
      INVALID_CREDENTIALS: 'Invalid credentials',
      ACCOUNT_INACTIVE: 'Account inactive',
      TWO_FACTOR_INVALID: 'Invalid two-factor authentication code',
      TWO_FACTOR_NOT_INITIALISED: 'Two-factor authentication not initialised',
      TWO_FACTOR_EXPIRED: 'Two-factor authentication expired'
    };

    if (lookup[normalized]) {
      return lookup[normalized];
    }

    return normalized
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatLoginType = (type) => {
    if (type === 'staff_login') return 'Staff Portal';
    return 'Client Portal';
  };

  const renderTwoFactorModal = () => {
    if (!twoFactorModalOpen) {
      return null;
    }

    const modalContent = (
      <div
        data-twofactor-modal
        className="fixed inset-0 flex items-center justify-center bg-black/50 px-4"
        style={{ zIndex: 9999 }}
      >
        <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-6 md:p-8">
          <button
            type="button"
            onClick={() => {
              setTwoFactorModalOpen(false);
              setTwoFactorSetupData(null);
              setTwoFactorConfirmCode('');
              setTwoFactorConfirmError('');
              setTwoFactorSetupLoading(false);
            }}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-gray-900">Set up Two-Factor Authentication</h3>
              <p className="mt-2 text-sm text-gray-600">
                Scan the QR code with Google Authenticator, Microsoft Authenticator, or any compatible app. Then enter the 6-digit code below.
              </p>

              <div className="mt-6 flex justify-center">
                {twoFactorSetupData?.otpauth ? (
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(twoFactorSetupData.otpauth)}`}
                    alt="Scan to set up two-factor authentication"
                    className="rounded-xl border border-gray-200 shadow-sm"
                  />
                ) : (
                  <div className="w-[220px] h-[220px] bg-gray-100 rounded-xl animate-pulse" />
                )}
              </div>

              {twoFactorSetupData?.secret && (
                <div className="mt-6">
                  <p className="text-xs uppercase text-gray-500 font-semibold">Manual entry code</p>
                  <div className="mt-2 inline-flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-800 rounded-lg font-mono text-sm tracking-widest">
                    {twoFactorSetupData.secret}
                  </div>
                </div>
              )}

              {twoFactorSetupData?.backupCodes && (
                <div className="mt-6">
                  <div className="flex items-center justify-between">
                    <p className="text-xs uppercase text-gray-500 font-semibold">Backup codes</p>
                    <button
                      type="button"
                      onClick={downloadBackupCodes}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Download codes
                    </button>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {twoFactorSetupData.backupCodes.map((code) => (
                      <div key={code} className="px-3 py-2 bg-white border border-gray-200 rounded-lg font-mono text-sm tracking-widest text-gray-700">
                        {code}
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Store these in a safe place. Each backup code can be used once if you lose access to your authenticator app.
                  </p>
                </div>
              )}
            </div>

            <div className="w-full md:w-72">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Enter the 6-digit code
              </label>
              <input
                type="text"
                value={twoFactorConfirmCode}
                onChange={(e) => setTwoFactorConfirmCode(e.target.value.replace(/[^0-9A-Za-z-]/g, ''))}
                inputMode="numeric"
                autoComplete="one-time-code"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg tracking-[0.4em]"
                placeholder="••••••"
              />
              {twoFactorConfirmError && (
                <p className="mt-2 text-sm text-red-600">{twoFactorConfirmError}</p>
              )}

              <button
                type="button"
                onClick={handleConfirmTwoFactorSetup}
                disabled={twoFactorSetupLoading || !twoFactorConfirmCode}
                className="mt-6 w-full inline-flex items-center justify-center px-4 py-3 border border-transparent rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {twoFactorSetupLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Verifying…
                  </>
                ) : (
                  'Verify & Enable'
                )}
              </button>

              <p className="mt-4 text-xs text-gray-500">
                Once verified, two-factor authentication will be enabled immediately for your account.
              </p>
            </div>
          </div>
        </div>
      </div>
    );

    if (twoFactorPortalRef.current) {
      return createPortal(modalContent, twoFactorPortalRef.current);
    }

    console.warn('[Settings] Portal container missing — rendering inline fallback.');
    return modalContent;
  };

  return (
    <div className="space-y-6">
      {renderTwoFactorModal()}
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">Manage your account, payments, and preferences</p>
      </div>

      {/* Message Display */}
      {message.text && (
        <div className={`p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              <span>{message.text}</span>
            </div>
            <button
              onClick={() => setMessage({ type: '', text: '' })}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-2">
        <nav className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-4 rounded-lg font-medium text-sm flex items-center gap-2 transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-[#1F11ED] text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[600px]">
        {/* Limited Access Notice for Additional Users */}
        {!isMainAccountHolder && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-yellow-800">Additional User Account</h3>
                <p className="text-sm text-yellow-700">
                  You have limited access to settings. Only the main account holder can manage Stripe Connect, subscriptions, notifications, security, and additional users.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Account Info Tab */}
        {activeTab === 'account' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Account Information</h3>
                {/* Only show Edit button for main account holders */}
                {isMainAccountHolder && (
                  <>
                    {!isEditingAccount ? (
                      <button
                        onClick={() => setIsEditingAccount(true)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </button>
                    ) : (
                      <div className="flex space-x-2">
                        <button
                          onClick={handleAccountSave}
                          disabled={loading}
                          className="inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                        >
                          {loading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              Save
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => setIsEditingAccount(false)}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </>
                )}
                {/* Info message for additional users */}
                {!isMainAccountHolder && (
                  <p className="text-sm text-gray-500 italic">
                    Your account information is managed by your administrator
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                    {!isMainAccountHolder && <span className="text-xs text-gray-500 ml-2">(Managed by account administrator)</span>}
                  </label>
                  <input
                    type="text"
                    value={accountInfo.firstName}
                    onChange={(e) => setAccountInfo(prev => ({ ...prev, firstName: e.target.value }))}
                    disabled={!isEditingAccount || !isMainAccountHolder}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      !isMainAccountHolder 
                        ? 'disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed' 
                        : 'disabled:bg-gray-50'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                    {!isMainAccountHolder && <span className="text-xs text-gray-500 ml-2">(Managed by account administrator)</span>}
                  </label>
                  <input
                    type="text"
                    value={accountInfo.lastName}
                    onChange={(e) => setAccountInfo(prev => ({ ...prev, lastName: e.target.value }))}
                    disabled={!isEditingAccount || !isMainAccountHolder}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      !isMainAccountHolder 
                        ? 'disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed' 
                        : 'disabled:bg-gray-50'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                    {!isMainAccountHolder && <span className="text-xs text-gray-500 ml-2">(Managed by account administrator)</span>}
                  </label>
                  <input
                    type="email"
                    value={accountInfo.email}
                    onChange={(e) => setAccountInfo(prev => ({ ...prev, email: e.target.value }))}
                    disabled={!isEditingAccount || !isMainAccountHolder}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      !isMainAccountHolder 
                        ? 'disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed' 
                        : 'disabled:bg-gray-50'
                    }`}
                  />
                  {!isMainAccountHolder && (
                    <p className="mt-1 text-xs text-gray-500">
                      Your email address is managed by your account administrator. Please contact them to update your email.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                    {!isMainAccountHolder && <span className="text-xs text-gray-500 ml-2">(Contact main account to change)</span>}
                  </label>
                  <input
                    type="text"
                    value={accountInfo.companyName}
                    onChange={(e) => setAccountInfo(prev => ({ ...prev, companyName: e.target.value }))}
                    disabled={!isEditingAccount || !isMainAccountHolder}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                    {!isMainAccountHolder && <span className="text-xs text-gray-500 ml-2">(Managed by account administrator)</span>}
                  </label>
                  <input
                    type="tel"
                    value={accountInfo.phone}
                    onChange={(e) => setAccountInfo(prev => ({ ...prev, phone: e.target.value }))}
                    disabled={!isEditingAccount || !isMainAccountHolder}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      !isMainAccountHolder 
                        ? 'disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed' 
                        : 'disabled:bg-gray-50'
                    }`}
                  />
                  {!isMainAccountHolder && (
                    <p className="mt-1 text-xs text-gray-500">
                      Contact your account administrator to update phone number.
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                    {!isMainAccountHolder && <span className="text-xs text-gray-500 ml-2">(Contact main account to change)</span>}
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Street Address"
                      value={accountInfo.address.street}
                      onChange={(e) => setAccountInfo(prev => ({ 
                        ...prev, 
                        address: { ...prev.address, street: e.target.value } 
                      }))}
                      disabled={!isEditingAccount || !isMainAccountHolder}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                    />
                    <input
                      type="text"
                      placeholder="City"
                      value={accountInfo.address.city}
                      onChange={(e) => setAccountInfo(prev => ({ 
                        ...prev, 
                        address: { ...prev.address, city: e.target.value } 
                      }))}
                      disabled={!isEditingAccount || !isMainAccountHolder}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                    />
                    <input
                      type="text"
                      placeholder="State/County"
                      value={accountInfo.address.state}
                      onChange={(e) => setAccountInfo(prev => ({ 
                        ...prev, 
                        address: { ...prev.address, state: e.target.value } 
                      }))}
                      disabled={!isEditingAccount || !isMainAccountHolder}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                    />
                    <input
                      type="text"
                      placeholder="ZIP/Postal Code"
                      value={accountInfo.address.zipCode}
                      onChange={(e) => setAccountInfo(prev => ({ 
                        ...prev, 
                        address: { ...prev.address, zipCode: e.target.value } 
                      }))}
                      disabled={!isEditingAccount || !isMainAccountHolder}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                    />
                    <div>
                      <select
                        value={accountInfo.address.country}
                        onChange={(e) => setAccountInfo(prev => ({ 
                          ...prev, 
                          address: { ...prev.address, country: e.target.value } 
                        }))}
                        disabled={!isEditingAccount || !isMainAccountHolder}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      >
                        {COUNTRIES.map(country => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Password Change Section */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Change Password</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                  <div className="relative">
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(!showPasswords)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPasswords ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                  <div className="relative">
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                  <div className="relative">
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <button
                  onClick={handlePasswordChange}
                  disabled={loading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Changing...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Change Password
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stripe Connected Account Tab - STRIPE CONNECT IMPLEMENTATION */}
        {activeTab === 'payment' && (
          <div className="space-y-6">
              {/* Stripe Connect Account Status */}
              {stripeAccountStatus ? (
                <div className="space-y-6">
                {/* Side-by-Side Layout: Stripe Account + Payment Method (when both enabled) */}
                {stripeAccountStatus.chargesEnabled && stripeAccountStatus.payoutsEnabled && isMainAccountHolder ? (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Left: Stripe Connected Account Summary */}
                      <div className="bg-white rounded-xl border border-gray-200 shadow-md p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <span className="mr-2">🏦</span>
                            Receiving Account
                          </h3>
                          <button
                            onClick={() => window.open(`https://dashboard.stripe.com/connect/accounts/${stripeAccountStatus.stripeAccountId}`, '_blank')}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
                            title="Open Stripe Dashboard"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <span className="text-sm font-medium text-gray-900">Active & Ready</span>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Account ID</div>
                            <div className="font-mono text-xs break-all text-gray-700">{stripeAccountStatus.stripeAccountId}</div>
                          </div>
                          <button
                            onClick={() => setShowAccountDetails(!showAccountDetails)}
                            className="w-full text-sm text-gray-600 hover:text-gray-900 py-2 border-t border-gray-200 flex items-center justify-center"
                          >
                            {showAccountDetails ? 'Hide Details' : 'View Full Details'}
                            {showAccountDetails ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
                          </button>
                        </div>
                      </div>

                      {/* Right: Payment Method for Invoices */}
                      <div className="bg-white rounded-xl border border-gray-200 shadow-md p-6">
                        <div className="flex items-center mb-4">
                          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <span className="mr-2">💳</span>
                            Payment Method
                          </h3>
                        </div>

                        {paymentMethodLoading ? (
                          <div className="text-center py-8">
                            <RefreshCw className="w-6 h-6 mx-auto animate-spin text-gray-400" />
                            <p className="mt-2 text-sm text-gray-600">Loading payment method...</p>
                          </div>
                        ) : defaultPaymentMethod ? (
                          <div className="space-y-4">
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                              <div className="flex items-center space-x-3">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                <div>
                                  <h4 className="font-medium text-green-900">Default Payment Method Set</h4>
                                  <p className="text-sm text-green-700 mt-1">
                                    {defaultPaymentMethod.paymentMethod?.card ? (
                                      <>
                                        {defaultPaymentMethod.paymentMethod.card.brand.toUpperCase()} •••• {defaultPaymentMethod.paymentMethod.card.last4}
                                        {' - Expires '}
                                        {defaultPaymentMethod.paymentMethod.card.expMonth.toString().padStart(2, '0')}/{defaultPaymentMethod.paymentMethod.card.expYear}
                                      </>
                                    ) : (
                                      'Payment method configured'
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2">
                              <button
                                onClick={async () => {
                                  setPaymentMethodLoading(true);
                                  try {
                                    if (stripeAccountStatus?.stripeAccountId) {
                                      // If we have customer ID, open directly to that customer's payment methods
                                      // Otherwise, open to customers list
                                      if (defaultPaymentMethod?.stripeCustomerId) {
                                        window.open(`https://dashboard.stripe.com/connect/accounts/${stripeAccountStatus.stripeAccountId}/customers/${defaultPaymentMethod.stripeCustomerId}`, '_blank');
                                        setMessage({ type: 'info', text: 'Opened Stripe Dashboard to your customer\'s payment methods. You can add, remove, or update payment methods here.' });
                                      } else {
                                        window.open(`https://dashboard.stripe.com/connect/accounts/${stripeAccountStatus.stripeAccountId}/customers`, '_blank');
                                        setMessage({ type: 'info', text: 'Opened Stripe Dashboard. Go to Customers → select your customer → Payment Methods tab to manage payment methods.' });
                                      }
                                    } else {
                                      setMessage({ type: 'error', text: 'Stripe account not found. Please set up your Stripe Connect account first.' });
                                    }
                                  } catch (error) {
                                    setMessage({ type: 'error', text: 'Failed to open Stripe dashboard' });
                                  } finally {
                                    setPaymentMethodLoading(false);
                                  }
                                }}
                                className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                              >
                                <ExternalLink className="w-4 h-4 mr-2" />
                                Manage Payment Methods
                              </button>
                              <button
                                onClick={async () => {
                                  if (!confirm('Are you sure you want to remove your default payment method? You will need to set one up before sending invoices.')) {
                                    return;
                                  }
                                  setPaymentMethodLoading(true);
                                  try {
                                    await api.delete('/api/payment-methods/default');
                                    setDefaultPaymentMethod(null);
                                    setMessage({ type: 'success', text: 'Default payment method removed successfully' });
                                  } catch (error) {
                                    setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to remove payment method' });
                                  } finally {
                                    setPaymentMethodLoading(false);
                                  }
                                }}
                                className="w-full inline-flex items-center justify-center px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Remove Payment Method
                              </button>
                            </div>
                            <p className="text-xs text-gray-500">
                              Used for "Pay Now" and "Pay on Due Date" when paying invoices.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                              <div className="flex items-center space-x-3">
                                <AlertCircle className="w-5 h-5 text-yellow-600" />
                                <div>
                                  <h4 className="font-medium text-yellow-900">No Payment Method Set</h4>
                                  <p className="text-sm text-yellow-700 mt-1">
                                    Set up a payment method before sending invoices.
                                  </p>
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                setShowPaymentMethodModal(true);
                              }}
                              className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                            >
                              <CreditCard className="w-4 h-4 mr-2" />
                              Set Up Payment Method
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Summary Section - Explains what each section does and why it's important */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                      {/* Receiving Account Summary */}
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                        <div className="flex items-center space-x-2 mb-3">
                          <span className="text-xl">🏦</span>
                          <h4 className="text-lg font-semibold text-gray-900">Receiving Account</h4>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <h5 className="text-sm font-medium text-gray-900 mb-1">What it does:</h5>
                            <p className="text-sm text-gray-700">
                              Enables you to receive payments from clients when they pay your invoices. This is where money from your invoices goes.
                            </p>
                          </div>
                          <div>
                            <h5 className="text-sm font-medium text-gray-900 mb-1">Why it's important:</h5>
                            <p className="text-sm text-gray-700">
                              Without a fully set up receiving account, you won't be able to receive funds from clients. Payments will fail and clients won't be able to complete their payments to you.
                            </p>
                          </div>
                          <div className="pt-2 border-t border-blue-200">
                            <p className="text-xs text-gray-600">
                              <strong>Setup required:</strong> Complete Stripe Connect onboarding with your bank account details.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Payment Method Summary */}
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                        <div className="flex items-center space-x-2 mb-3">
                          <span className="text-xl">💳</span>
                          <h4 className="text-lg font-semibold text-gray-900">Payment Method</h4>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <h5 className="text-sm font-medium text-gray-900 mb-1">What it does:</h5>
                            <p className="text-sm text-gray-700">
                              Your default payment method for paying invoices you receive from others. This card will be used automatically for "Pay Now" and "Pay on Due Date" actions.
                            </p>
                          </div>
                          <div>
                            <h5 className="text-sm font-medium text-gray-900 mb-1">Why it's important:</h5>
                            <p className="text-sm text-gray-700">
                              You need a payment method set up to pay invoices you receive. Without it, you won't be able to use "Pay Now" or "Pay on Due Date" features.
                            </p>
                          </div>
                          <div className="pt-2 border-t border-blue-200">
                            <p className="text-xs text-gray-600">
                              <strong>Setup required:</strong> Add a payment method in the card above. It's saved securely for all future payments.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                  ) : stripeAccountStatus.chargesEnabled && stripeAccountStatus.payoutsEnabled ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <CheckCircle className="w-6 h-6 text-green-600" />
                        <div>
                          <h4 className="text-lg font-medium text-green-900">✅ Stripe Payments Ready</h4>
                        </div>
                      </div>
                      
                      <div className="space-y-3 mb-6">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">💰</span>
                          <span className="text-green-800 font-medium">You can RECEIVE payments</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">💳</span>
                          <span className="text-green-800 font-medium">You can MAKE payments</span>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={() => window.open(`https://dashboard.stripe.com/connect/accounts/${stripeAccountStatus.stripeAccountId}`, '_blank')}
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Stripe Connected Account
                        </button>
                        <button
                          onClick={() => setShowAccountDetails(!showAccountDetails)}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          {showAccountDetails ? 'Hide Details' : 'View Account IDs'} 
                          {showAccountDetails ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <AlertCircle className="w-6 h-6 text-yellow-600" />
                        <div>
                          <h4 className="text-lg font-medium text-yellow-900">⚠️ Stripe Setup Required</h4>
                        </div>
                      </div>
                      
                      <div className="space-y-3 mb-6">
                        <div className="text-yellow-800">
                          <p className="font-medium mb-2">To RECEIVE payments, you need to:</p>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            <li>Complete Stripe account setup</li>
                            <li>Add bank account details</li>
                          </ul>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">💳</span>
                          <span className="text-yellow-800 font-medium">You can already MAKE payments</span>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={linkStripeAccount}
                          disabled={stripeLoading}
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50"
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          {stripeLoading ? 'Connecting...' : 'Complete Setup'}
                        </button>
                        <button
                          onClick={() => setShowAccountDetails(!showAccountDetails)}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          {showAccountDetails ? 'Hide Details' : 'View Account IDs'} 
                          {showAccountDetails ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Progressive Disclosure - Account Details (only show if account is set up) */}
                  {showAccountDetails && stripeAccountStatus && (stripeAccountStatus.chargesEnabled && stripeAccountStatus.payoutsEnabled) && (
                    <div className="bg-gray-50 rounded-lg p-6 space-y-6 mt-6">
                      <h5 className="font-medium text-gray-900 text-lg">📋 Account Information</h5>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Receiving Account */}
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center space-x-2 mb-3">
                            <span className="text-lg">🏦</span>
                            <h6 className="font-medium text-gray-900">Receiving Account</h6>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <label className="text-xs text-gray-500 uppercase tracking-wide">Account ID</label>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded flex-1 break-all">
                                  {stripeAccountStatus.stripeAccountId}
                                </span>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(stripeAccountStatus.stripeAccountId);
                                    setMessage({ type: 'success', text: 'Account ID copied to clipboard!' });
                                  }}
                                  className="text-xs bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded"
                                >
                                  Copy
                                </button>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-gray-600">Type:</span>
                                <span className="ml-1 font-medium">Standard</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Country:</span>
                                <span className="ml-1 font-medium">GB</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Charges:</span>
                                <span className={`ml-1 font-medium ${stripeAccountStatus.chargesEnabled ? 'text-green-600' : 'text-red-600'}`}>
                                  {stripeAccountStatus.chargesEnabled ? '✅ Enabled' : '❌ Disabled'}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">Payouts:</span>
                                <span className={`ml-1 font-medium ${stripeAccountStatus.payoutsEnabled ? 'text-green-600' : 'text-red-600'}`}>
                                  {stripeAccountStatus.payoutsEnabled ? '✅ Enabled' : '❌ Disabled'}
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={() => window.open(`https://dashboard.stripe.com/connect/accounts/${stripeAccountStatus.stripeAccountId}`, '_blank')}
                              className="w-full text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 px-3 py-2 rounded"
                            >
                              View in Stripe Dashboard
                            </button>
                            <p className="text-xs text-gray-500">
                              💡 For getting paid by clients
                            </p>
                          </div>
                        </div>

                        {/* Customer Account */}
                        {stripePaymentInfo && stripePaymentInfo.stripeCustomerId && (
                          <div className="bg-white rounded-lg p-4 border border-gray-200">
                            <div className="flex items-center space-x-2 mb-3">
                              <span className="text-lg">💳</span>
                              <h6 className="font-medium text-gray-900">Customer Account</h6>
                            </div>
                            <div className="space-y-3">
                              <div>
                                <label className="text-xs text-gray-500 uppercase tracking-wide">Customer ID</label>
                                <div className="flex items-center space-x-2 mt-1">
                                  <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded flex-1 break-all">
                                    {stripePaymentInfo.stripeCustomerId}
                                  </span>
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(stripePaymentInfo.stripeCustomerId);
                                      setMessage({ type: 'success', text: 'Customer ID copied to clipboard!' });
                                    }}
                                    className="text-xs bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded"
                                  >
                                    Copy
                                  </button>
                                </div>
                              </div>
                              <div className="space-y-2 text-sm">
                                <div>
                                  <span className="text-gray-600">Status:</span>
                                  <span className="ml-1 font-medium text-green-600">✅ Active</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Email:</span>
                                  <span className="ml-1 font-medium text-xs break-all">{stripePaymentInfo.userEmail}</span>
                                </div>
                              </div>
                              <button
                                onClick={() => window.open(`https://dashboard.stripe.com/customers/${stripePaymentInfo.stripeCustomerId}`, '_blank')}
                                className="w-full text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 px-3 py-2 rounded"
                              >
                                View in Stripe Dashboard
                              </button>
                              <p className="text-xs text-gray-500">
                                💡 For making payments to others
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                </div>
              ) : stripeAccountStatus?.timeout ? (
                <div className="space-y-6">
                  {/* Timeout - Account may exist */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <AlertCircle className="w-6 h-6 text-blue-600" />
                      <div className="flex-1">
                        <h4 className="text-lg font-medium text-blue-900">Unable to Verify Account Status</h4>
                        <p className="text-sm text-blue-700 mt-1">{stripeAccountStatus.message}</p>
                        {stripeAccountStatus.helpLink && (
                          <a 
                            href={stripeAccountStatus.helpLink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 mt-2 underline"
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Check Stripe Dashboard
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-4">
                    <button
                      onClick={checkStripeAccountStatus}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Retry
                    </button>
                    <a
                      href="https://dashboard.stripe.com/connect/accounts"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open Stripe Dashboard
                    </a>
                  </div>
                </div>
              ) : stripeLoading ? (
                <div className="space-y-6">
                  {/* Loading State - Prevents flash of "No Stripe Connected" */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-md p-6">
                    <div className="text-center py-8">
                      <RefreshCw className="w-8 h-8 mx-auto animate-spin text-blue-600" />
                      <p className="mt-4 text-sm font-medium text-gray-900">Loading Stripe account status...</p>
                      <p className="mt-2 text-xs text-gray-500">Please wait while we check your account</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* No Stripe Account Linked */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <AlertCircle className="w-6 h-6 text-yellow-600" />
                      <div>
                        <h4 className="text-lg font-medium text-yellow-900">No Stripe Account Connected</h4>
                        <p className="text-sm text-yellow-700">Connect your Stripe account to enable automatic payment collection on invoice due dates</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <h3 className="text-sm font-medium text-blue-800 mb-2">What you'll need:</h3>
                        <ul className="text-sm text-blue-700 space-y-1">
                          <li>• An existing Stripe account (create one at <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" className="underline">stripe.com</a>)</li>
                          <li>• Your business information and bank account details</li>
                          <li>• Government-issued ID for verification</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="text-center py-8">
                    <button
                      onClick={linkStripeAccount}
                      disabled={stripeLoading}
                      className="inline-flex items-center px-6 py-3 border border-transparent rounded-md text-base font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    >
                      {stripeLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Connecting...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-5 h-5 mr-2" />
                          Connect Stripe Account
                        </>
                      )}
                    </button>
                    <p className="text-sm text-gray-600 mt-3">
                      Secure payment processing via Stripe Connect
                    </p>
                  </div>
                </div>
              )}

              {/* Payment Settings removed – auto-collection is always enabled in backend */}
          </div>
        )}

        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <div className="space-y-6">
            {isInactiveAccount && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 sm:px-6 sm:py-5 flex flex-col gap-3">
                <div className="flex items-start space-x-3">
                  <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                    <AlertCircle className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="text-base font-semibold text-amber-900">Your account is inactive</h3>
                    <p className="text-sm text-amber-800">
                      Reactivate your subscription to restore Receevit® features. Choose the Essential plan to pick up where you left off, or upgrade to Pro for the full Receevit® experience.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {isInactiveAccount && (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-white p-6 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                        Pro Plan · £49.99/month
                      </span>
                      <h4 className="mt-3 text-lg font-bold text-gray-900">Unlock everything Receevit® offers</h4>
                      <p className="mt-2 text-sm text-gray-600">
                        Reactivate with Pro to regain chat, advanced analytics, and multi-user access immediately.
                      </p>
                    </div>
                    <span className="inline-flex items-center rounded-full bg-indigo-600 px-3 py-1 text-xs font-semibold text-white shadow-sm">
                      Recommended
                    </span>
                  </div>
                  <ul className="mt-4 space-y-2 text-sm text-gray-700">
                    {PRO_PLAN_FEATURE_PREVIEW.map((feature, index) => (
                      <li key={index} className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-blue-600" />
                        <span>{feature}</span>
                      </li>
                    ))}
                    <li className="flex items-center space-x-2 text-sm text-blue-600">
                      <span>+ everything in Essential</span>
                    </li>
                  </ul>
                  <button
                    onClick={() => openPlanChangeModal('Pro')}
                    className="mt-6 inline-flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Upgrade to Pro now
                  </button>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
                    Essential Plan · £39.99/month
                  </span>
                  <h4 className="mt-3 text-lg font-semibold text-gray-900">Keep things streamlined</h4>
                  <p className="mt-2 text-sm text-gray-600">
                    Perfect if you’re not ready for Pro yet—reactivate your core invoicing and automation tools.
                  </p>
                  <ul className="mt-4 space-y-2 text-sm text-gray-700">
                    {ESSENTIAL_PLAN_FEATURE_PREVIEW.map((feature, index) => (
                      <li key={index} className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => openPlanChangeModal('Essential')}
                    className="mt-6 inline-flex w-full items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
                  >
                    Reactivate on Essential
                  </button>
                </div>
              </div>
            )}
            <div className="bg-white rounded-xl border border-gray-200 shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Current Plan</h3>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-lg font-bold text-gray-900">
                      {formatPlanLabel(subscription.plan)}{formatPlanLabel(subscription.plan) === 'No Plan' ? '' : ' Plan'}
                    </h4>
                    <p className="text-xl font-bold text-blue-600">
                      {formatPlanLabel(subscription.plan) === 'No Plan'
                        ? '—'
                        : `${formatCurrency(subscription.price)}/month`}
                    </p>
                    {isSubscriptionCancelled && subscription.lastPlan && (
                      <p className="text-sm text-gray-600 mt-1">
                        Last active plan: {subscription.lastPlan}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      {subscriptionStatusLabel}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <h5 className="text-sm font-medium text-gray-700 mb-3">Plan Features:</h5>
                <ul className="space-y-2">
                  {subscription.features.map((feature, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                {isSubscriptionCancelled && (
                  <p className="mt-4 text-sm text-red-600">
                    Your subscription is cancelled. Choose a plan below to regain access to Receevit® features.
                  </p>
                )}
              </div>

              {/* Payment Method Management */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h5 className="text-sm font-semibold text-gray-900 mb-2">Payment Method</h5>
                <p className="text-xs text-gray-600 mb-4">
                  Your payment method is securely stored with Stripe. This card is used for your subscription and invoice payments.
                </p>
                <button
                  onClick={async () => {
                    try {
                      setLoading(true);
                      const response = await callAuthPostWithFallback(api, {
                        apiPath: '/stripe-connect/create-portal-session',
                        fallbackPath: '/stripe-connect/create-portal-session',
                        method: 'post',
                        payload: {
                          returnUrl: window.location.href
                        },
                        allowFallbackOnProduction: true,
                        forceFallback: true,
                        fallbackBaseUrlOverride: authFallbackInternal.AUTH_LAMBDA_FUNCTION_URL
                      });
                      if (response?.success && response?.url) {
                        window.location.href = response.url;
                      } else {
                        setMessage({ type: 'error', text: response?.error || 'Failed to open payment portal' });
                      }
                    } catch (err) {
                      console.error('Error opening payment portal:', err);
                      setMessage({ type: 'error', text: 'Failed to open payment portal' });
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {loading ? 'Loading...' : 'Manage Payment Methods'}
                </button>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                {(!isProPlan || isInactiveAccount) && (
                  <button
                    onClick={() => openPlanChangeModal('Pro')}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    {loading ? 'Updating…' : isInactiveAccount ? 'Upgrade to Pro' : 'Upgrade to Pro'}
                  </button>
                )}
                {!isInactiveAccount && isProPlan && (
                  <button
                    onClick={() => openPlanChangeModal('Essential')}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    {loading ? 'Downgrading…' : 'Downgrade to Essential'}
                  </button>
                )}
                {!isInactiveAccount && (
                  <button
                    onClick={() => setCancelModal({ open: true, confirming: false })}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                  >
                    Cancel Subscription
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Notification Preferences</h3>
              </div>
              <div className="space-y-4">
                {NOTIFICATION_TOGGLE_CONFIG.map(({ key, label, description }) => {
                  const value = notifications[key] ?? NOTIFICATION_DEFAULTS[key];
                  return (
                    <div key={key} className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-700">
                          {label}
                        </label>
                        <p className="text-xs text-gray-500">{description}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={() => handleNotificationToggle(key)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
            
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 shadow-md p-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900">Two-Factor Authentication</h3>
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                        security.twoFactor.enabled
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}
                    >
                      {security.twoFactor.enabled ? 'Enabled' : 'Pending'}
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-gray-600">
                    Protect your account with an authenticator app. You'll enter a 6-digit code each time you sign in.
                  </p>
                  {!security.twoFactor.enabled && (
                    <p
                      className={`mt-2 text-sm rounded-lg px-3 py-2 inline-flex items-center gap-2 ${
                        security.twoFactor.reconfigureRequired || twoFactorReconfiguring
                          ? 'text-blue-800 bg-blue-50 border border-blue-200'
                          : security.twoFactor.daysRemaining > 0
                          ? 'text-yellow-700 bg-yellow-50 border border-yellow-200'
                          : 'text-red-700 bg-red-50 border border-red-200'
                      }`}
                    >
                      <AlertCircle className="w-4 h-4" />
                      {security.twoFactor.reconfigureRequired || twoFactorReconfiguring
                        ? 'Two-factor authentication needs to be reconfigured. Scan the new QR code and verify the 6-digit code now to continue using Receevit®.'
                        : security.twoFactor.daysRemaining > 0
                        ? `Set up two-factor authentication within the next ${security.twoFactor.daysRemaining} day${security.twoFactor.daysRemaining === 1 ? '' : 's'}.`
                        : 'Two-factor authentication is required. Please set it up now to keep access.'}
                    </p>
                  )}
                  {security.twoFactor.enabled && security.twoFactor.verifiedAt && (
                    <p className="mt-2 text-sm text-gray-500">
                      Verified on {new Date(security.twoFactor.verifiedAt).toLocaleString()}
                    </p>
                  )}
                  {security.twoFactor.gracePeriodEndsAt &&
                    !security.twoFactor.enabled &&
                    !twoFactorReconfiguring &&
                    !security.twoFactor.reconfigureRequired && (
                    <p className="mt-2 text-xs text-gray-500">
                      Grace period ends {new Date(security.twoFactor.gracePeriodEndsAt).toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-stretch gap-3 min-w-[220px]">
                  <button
                    onClick={handleStartTwoFactorSetup}
                    disabled={twoFactorSetupLoading}
                    className={`inline-flex justify-center items-center px-4 py-2 rounded-md text-sm font-medium border ${
                      security.twoFactor.enabled
                        ? 'border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100'
                        : 'border-transparent text-white bg-blue-600 hover:bg-blue-700'
                    } disabled:opacity-50`}
                  >
                    {twoFactorSetupLoading ? (
                      <>
                        <div className={`animate-spin rounded-full h-4 w-4 border-b-2 mr-2 ${
                          security.twoFactor.enabled ? 'border-blue-700' : 'border-white'
                        }`}></div>
                        Preparing…
                      </>
                    ) : (
                      <>
                        <Shield className="w-4 h-4 mr-2" />
                        {security.twoFactor.enabled ? 'Reconfigure 2FA' : 'Set up 2FA'}
                      </>
                    )}
                  </button>
                  {security.twoFactor.enabled && (
                    <p className="text-xs text-gray-500 text-center">
                      Reconfiguring will generate a new secret and backup codes.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Login History - Only show for main account holders */}
            {isMainAccountHolder && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Login History</h3>
              {security.loginHistory.length === 0 ? (
                <div className="p-4 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 text-center">
                  No recent logins recorded yet.
                </div>
              ) : (
                <div className="space-y-4">
                  {security.loginHistory.slice(0, 15).map((login, index) => {
                    const isSuccess = login.success !== false;
                    const statusBadgeClass = isSuccess
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-700';
                    const statusText = isSuccess ? 'Successful' : 'Failed';
                    const iconWrapperClass = isSuccess
                      ? 'bg-green-100 text-green-600'
                      : 'bg-red-100 text-red-600';
                    const IconComponent = isSuccess ? CheckCircle : AlertCircle;
                    const locationLine = [formatLoginType(login.loginType), login.device].filter(Boolean).join(' • ');

                    return (
                      <div key={index} className="flex items-start justify-between p-4 bg-gray-50 rounded-lg border border-gray-100">
                        <div className="flex items-start space-x-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${iconWrapperClass}`}>
                            <IconComponent className="w-4 h-4" />
                          </div>
                          <div className="space-y-1">
                            <p className="text-sm font-semibold text-gray-900">{login.userName || login.device}</p>
                            {login.userEmail && (
                              <p className="text-xs text-gray-500">{login.userEmail}</p>
                            )}
                            <p className="text-xs text-gray-500">{locationLine}</p>
                            <p className="text-xs text-gray-500">{login.location}</p>
                            <p className="text-xs text-gray-400">IP: {login.ip}</p>
                            {!isSuccess && (
                              <p className="text-xs text-red-500 mt-1">
                                Reason: {formatFailureReason(login.failureReason)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end space-y-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass}`}>
                            {statusText}
                          </span>
                          <p className="text-sm text-gray-900">{formatDateTime(login.date)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            )}
          </div>
        )}

        {/* Additional Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Pro Plan Check */}
            {subscription.plan === 'Essential' ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <div className="flex items-center space-x-3">
                  <Package className="w-6 h-6 text-yellow-600" />
                  <div>
                    <h3 className="text-lg font-semibold text-yellow-800">Upgrade to Pro Plan</h3>
                    <p className="text-yellow-700">
                      Additional users feature is only available on the Pro plan (£49.99/month). 
                      Upgrade to add up to 4 additional users to your account.
                    </p>
                    <button
                      onClick={() => openPlanChangeModal('Pro')}
                      className="mt-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700"
                    >
                      <Package className="w-4 h-4 mr-2" />
                      Upgrade to Pro
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* User Management Header */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-md p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Additional Users</h3>
                      <p className="text-sm text-gray-600">
                        Manage additional users on your account. You can add up to 4 additional users.
                      </p>
                    </div>
                    {additionalUsers.length < 4 && (
                      <button
                        onClick={() => setShowAddUserForm(!showAddUserForm)}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add User
                      </button>
                    )}
                  </div>

                  {/* User Count Display */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        Users: {additionalUsers.length}/4
                      </span>
                      {additionalUsers.length >= 4 && (
                        <span className="text-sm text-red-600 font-medium">
                          Maximum users reached
                        </span>
                      )}
                    </div>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          additionalUsers.length >= 4 ? 'bg-red-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${(additionalUsers.length / 4) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Add User Form */}
                  {showAddUserForm && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Add New User</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                          <input
                            type="text"
                            value={newUserForm.name}
                            onChange={(e) => setNewUserForm(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Enter full name"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                          <input
                            type="email"
                            value={newUserForm.email}
                            onChange={(e) => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="Enter email address"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                      <div className="mt-4 flex space-x-3">
                        <button
                          onClick={handleAddUser}
                          disabled={loading || !newUserForm.name.trim() || !newUserForm.email.trim()}
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                        >
                          {loading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Sending...
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4 mr-2" />
                              Send Invite
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setShowAddUserForm(false);
                            setNewUserForm({ name: '', email: '' });
                          }}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Users Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            User
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Role
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Invited
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {additionalUsers.map((user) => (
                          <tr key={user.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                <div className="text-sm text-gray-500">{user.email}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-900">
                                {user.role || 'Not specified'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                user.status === 'active' || user.status === 'Active'
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {user.status === 'invited' ? 'Pending Invite' : user.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(user.createdAt || user.invitedAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                {user.status === 'invited' && (
                                  <button
                                    onClick={() => handleResendInvite(user.id)}
                                    disabled={loading}
                                    className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                                    title="Resend invite"
                                  >
                                    <RefreshCw className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleRemoveUser(user.id)}
                                  disabled={loading}
                                  className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                  title="Remove user"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Empty State */}
                  {additionalUsers.length === 0 && (
                    <div className="text-center py-8">
                      <Users className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No additional users</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Get started by adding your first additional user to your account.
                      </p>
                      <div className="mt-6">
                        <button
                          onClick={() => setShowAddUserForm(true)}
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add User
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {planChangeModal.open && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {isReactivationFlow
                    ? 'Confirm Reactivation'
                    : planChangeModal.plan === 'Pro'
                    ? 'Confirm Upgrade'
                    : 'Confirm Downgrade'}
                </h3>
                <button
                  onClick={closePlanChangeModal}
                  disabled={planChangeModal.confirming || loading}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Close plan change confirmation"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4 text-sm">
                <p className="text-gray-600">
                  {planChangeModal.plan === 'Pro'
                    ? 'You’re about to upgrade to the Pro plan. You’ll unlock chat, enhanced reporting, multi-user access, and priority support immediately.'
                    : isReactivationFlow
                    ? 'You’re about to reactivate your Essential plan. Access to Receevit®’s core features will resume right away.'
                    : 'You’re about to downgrade to the Essential plan. You’ll lose access to Pro-only features such as the in-app chat experience and additional team seats.'}
                </p>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-blue-800">
                  <p className="font-medium mb-2">
                    {planChangeModal.plan === 'Pro'
                      ? 'Key Pro benefits:'
                      : isReactivationFlow
                      ? 'When you reactivate:'
                      : 'Before you downgrade:'}
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    {planChangeModal.plan === 'Pro' ? (
                      <>
                        <li>Chat with your clients directly inside Receevit®</li>
                        <li>Advanced reporting dashboards and insights</li>
                        <li>Up to 5 team members with shared access</li>
                        <li>Priority support with faster response times</li>
                      </>
                    ) : isReactivationFlow ? (
                      <>
                        <li>Your dashboard, invoices, and payment automations will resume immediately</li>
                        <li>You can upgrade to Pro at any time for chat, multi-user access, and advanced analytics</li>
                        <li>No data has been deleted—everything picks up where you left off</li>
                      </>
                    ) : (
                      <>
                        <li>Chat and multi-user access are Pro-only—those experiences will be disabled</li>
                        <li>Only the main account holder will retain access</li>
                        <li>You can re-upgrade at any time to restore Pro features instantly</li>
                      </>
                    )}
                  </ul>
                </div>
                <p className="text-xs text-gray-500">
                  {planChangeModal.plan === 'Pro'
                    ? 'The change is applied immediately—Stripe issues a prorated credit (if applicable) and bills you at the Pro rate going forward.'
                    : isReactivationFlow
                    ? 'Reactivation is instant. Stripe restarts your subscription and normal billing resumes from today. Automation and notifications switch back on straight away.'
                    : 'The change is applied immediately—Stripe issues a prorated credit for the unused portion of your Pro plan and bills you at the Essential rate going forward. No data is deleted, so you can upgrade again whenever you need the additional features.'}
                </p>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={closePlanChangeModal}
                  disabled={planChangeModal.confirming || loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isReactivationFlow
                    ? 'Stay inactive'
                    : 'Keep current plan'}
                </button>
                <button
                  onClick={confirmPlanChange}
                  disabled={planChangeModal.confirming || loading}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {planChangeModal.confirming || loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Applying…
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {isReactivationFlow
                        ? 'Reactivate now'
                        : 'Yes, change my plan'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {cancelModal.open && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <AlertTriangle className="w-5 h-5 text-red-600 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900">Confirm Subscription Cancellation</h3>
                </div>
                <button
                  onClick={() => setCancelModal({ open: false, confirming: false })}
                  disabled={cancelModal.confirming}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Close cancellation confirmation"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-700">
                You’re about to cancel your Receevit® subscription. Your account status will change to inactive immediately, Stripe will stop future billing, and access to paywalled features will be disabled.
              </p>
              <ul className="text-sm text-gray-600 space-y-1 pl-5 list-disc">
                <li>All team members will lose access to Pro-only features</li>
                <li>Automated payments and invoice approvals will pause</li>
                <li>You can upgrade again at any time to restore service</li>
              </ul>
              <p className="text-xs text-gray-500">
                Have questions? Contact support before cancelling to avoid service interruption.
              </p>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setCancelModal({ open: false, confirming: false })}
                  disabled={cancelModal.confirming}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Keep subscription
                </button>
                <button
                  onClick={handleCancelSubscription}
                  disabled={cancelModal.confirming}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cancelModal.confirming ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Cancelling…
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Yes, cancel my subscription
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Method Setup Modal */}
      {showPaymentMethodModal && (
        <PaymentMethodSetupModal
          isOpen={showPaymentMethodModal}
          onClose={() => {
            setShowPaymentMethodModal(false);
          }}
          onSuccess={async () => {
            // Refresh the default payment method after successful setup
            // Add small delay to ensure database write is committed
            try {
              setPaymentMethodLoading(true);
              // Wait 500ms to ensure database write is committed
              await new Promise(resolve => setTimeout(resolve, 500));
              const response = await api.get('/api/payment-methods/default');
              if (response.data.success) {
                setDefaultPaymentMethod(response.data.data);
                setMessage({ type: 'success', text: 'Payment method set up successfully!' });
              } else {
                // If still not found, try again after a longer delay
                await new Promise(resolve => setTimeout(resolve, 1000));
                const retryResponse = await api.get('/api/payment-methods/default');
                if (retryResponse.data.success) {
                  setDefaultPaymentMethod(retryResponse.data.data);
                  setMessage({ type: 'success', text: 'Payment method set up successfully!' });
                }
              }
            } catch (error) {
              console.error('Error refreshing payment method:', error);
              // Don't show error if payment method was just set up - might be a timing issue
              if (error.response?.status !== 404) {
                setMessage({ type: 'warning', text: 'Payment method saved but refresh failed. Please reload the page.' });
              }
            } finally {
              setPaymentMethodLoading(false);
            }
          }}
        />
      )}
    </div>
  );
};

// Payment Method Setup Modal Component
const PaymentMethodSetupModal = ({ isOpen, onClose, onSuccess }) => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [setupIntentData, setSetupIntentData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && !clientSecret && !loading) {
      // Create SetupIntent when modal opens
      const createSetupIntent = async () => {
        try {
          setLoading(true);
          setError(null);
          const response = await api.post('/api/payment-methods/setup-intent');
          
          if (response.data.success) {
            setClientSecret(response.data.data.clientSecret);
            setSetupIntentData(response.data.data);
          } else {
            setError(response.data.error || 'Failed to initialize payment method setup');
          }
        } catch (err) {
          console.error('Error creating SetupIntent:', err);
          setError(err.response?.data?.error || 'Failed to initialize payment method setup');
        } finally {
          setLoading(false);
        }
      };
      
      createSetupIntent();
    } else if (!isOpen) {
      // Reset when modal closes
      setClientSecret(null);
      setSetupIntentData(null);
      setError(null);
      setLoading(false);
    }
  }, [isOpen, clientSecret, loading]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements || !clientSecret) {
      setError('Stripe is not loaded. Please wait a moment and try again.');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Confirm SetupIntent
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required'
      });

      if (confirmError) {
        setError(confirmError.message || 'Failed to save payment method. Please try again.');
        setProcessing(false);
        return;
      }

      if (setupIntent.status === 'succeeded' && setupIntent.payment_method) {
        // Save as default payment method
        // Payment method is on connected account (SetupIntent was created there)
        const saveResponse = await api.post('/api/payment-methods/default', {
          stripePaymentMethodId: setupIntent.payment_method, // Payment method ID (on connected account)
          connectedAccountId: setupIntentData.connectedAccountId, // Connected account where payment method is stored
          stripeCustomerId: setupIntentData.customerId // Customer on connected account
        });

        if (saveResponse.data.success) {
          // Reset state
          setClientSecret(null);
          setSetupIntentData(null);
          setProcessing(false);
          onSuccess();
          onClose();
        } else {
          setError(saveResponse.data.error || 'Payment method saved but failed to set as default. Please try again.');
          setProcessing(false);
        }
      } else {
        setError('Setup incomplete. Please try again.');
        setProcessing(false);
      }
    } catch (err) {
      console.error('Error setting up payment method:', err);
      setError(err.response?.data?.error || 'An unexpected error occurred. Please try again.');
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Set Up Payment Method</h3>
            <button
              onClick={onClose}
              disabled={processing || loading}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-6">
            Add a payment method that will be used for "Pay Now" and "Pay on Due Date" when paying invoices.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 mx-auto animate-spin text-gray-400 mb-4" />
              <p className="text-sm text-gray-600">Initializing payment form...</p>
            </div>
          ) : clientSecret ? (
            <Elements 
              stripe={stripePromise} 
              options={{ 
                clientSecret,
                appearance: {
                  theme: 'stripe',
                }
              }}
              onLoadError={(error) => {
                console.error('❌ Elements provider load error:', error);
                console.error('❌ Elements error keys:', Object.keys(error || {}));
              }}
            >
              <PaymentMethodForm
                clientSecret={clientSecret}
                setupIntentData={setupIntentData}
                onSuccess={onSuccess}
                onClose={onClose}
                onError={(err) => setError(err)}
              />
            </Elements>
          ) : error ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
              <p className="text-sm text-red-600 mb-4">{error}</p>
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// Inner form component that uses Stripe hooks
const PaymentMethodForm = ({ clientSecret, setupIntentData, onSuccess, onClose, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements || !clientSecret) {
      onError('Stripe is not loaded. Please wait a moment and try again.');
      return;
    }

    setProcessing(true);
    onError(null);

    try {
      // Confirm SetupIntent
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required'
      });

      if (confirmError) {
        onError(confirmError.message || 'Failed to save payment method. Please try again.');
        setProcessing(false);
        return;
      }

      if (setupIntent.status === 'succeeded' && setupIntent.payment_method) {
        // Save as default payment method
        // CRITICAL: Payment method is on PLATFORM account (SetupIntent was created on platform)
        // The payment method ID from setupIntent.payment_method is the platform payment method ID
        // setupIntentData.customerId is the platform customer ID
        const saveResponse = await api.post('/api/payment-methods/default', {
          stripePaymentMethodId: setupIntent.payment_method, // Platform payment method ID
          connectedAccountId: setupIntentData.connectedAccountId, // User's connected account (where we'll clone it)
          platformCustomerId: setupIntentData.customerId || setupIntentData.platformCustomerId // Platform customer ID
        });

        if (saveResponse.data.success) {
          setProcessing(false);
          onSuccess();
          onClose();
        } else {
          onError(saveResponse.data.error || 'Payment method saved but failed to set as default. Please try again.');
          setProcessing(false);
        }
      } else {
        onError('Setup incomplete. Please try again.');
        setProcessing(false);
      }
    } catch (err) {
      console.error('Error setting up payment method:', err);
      onError(err.response?.data?.error || 'An unexpected error occurred. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="border border-gray-200 rounded-lg p-4">
        <PaymentElement
          options={{
            layout: 'tabs',
            paymentMethodOrder: ['card'],
            fields: {
              billingDetails: 'auto'
            }
          }}
          onLoadError={(error) => {
            console.error('❌ PaymentElement load error:', error);
            const errorKeys = Object.keys(error || {});
            console.error('❌ Error object keys:', errorKeys);
            
            // Log each key and its value
            errorKeys.forEach(key => {
              console.error(`❌ Error.${key}:`, error[key]);
            });
            
            // Try to extract error message from various possible structures
            let errorMessage = 'Failed to load payment form.';
            if (error?.message) {
              errorMessage = error.message;
            } else if (error?.type) {
              errorMessage = `Error type: ${error.type}`;
            } else if (error?.code) {
              errorMessage = `Error code: ${error.code}`;
            } else if (typeof error === 'string') {
              errorMessage = error;
            } else if (errorKeys.length > 0) {
              // If we have keys but no standard properties, show the first key's value
              const firstKey = errorKeys[0];
              errorMessage = `Error: ${firstKey} = ${JSON.stringify(error[firstKey])}`;
            }
            
            console.error('❌ ClientSecret preview:', clientSecret ? `${clientSecret.substring(0, 30)}...` : 'missing');
            console.error('❌ SetupIntent ID from clientSecret:', clientSecret ? clientSecret.split('_secret_')[0] : 'missing');
            
            onError(errorMessage);
          }}
          onReady={() => {
            console.log('✅ PaymentElement loaded successfully');
          }}
        />
      </div>

      <div className="flex items-center space-x-2 text-xs text-gray-500">
        <Shield className="w-4 h-4" />
        <span>Your payment information is secure and encrypted</span>
      </div>

      <div className="flex space-x-3 pt-4">
        <button
          type="button"
          onClick={onClose}
          disabled={processing}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {processing ? 'Saving...' : 'Save Payment Method'}
        </button>
      </div>
    </form>
  );
};

// Memoize Settings component to prevent unnecessary re-renders
export default memo(Settings);
